import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import Flag, { AutoDismissFlag } from '@atlaskit/flag';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { token } from '@atlaskit/tokens';

import { ComponentFlagProps } from 'src/redux/flags/types';
import { CREATE_BRANCH_ERROR_TYPE } from 'src/sections/create-branch/constants';
import { CreateBranchError } from 'src/sections/create-branch/types';
import { isCreateBranchErrorDisplayedInline } from 'src/sections/create-branch/utils';
import { BucketState } from 'src/types/state';
import { urls } from 'src/urls/global';

import { messages } from './create-branch-error-flag.i18n';

const errorMessage = (error: CreateBranchError) => {
  if (isCreateBranchErrorDisplayedInline(error.type)) {
    return null;
  }
  switch (error.type) {
    case CREATE_BRANCH_ERROR_TYPE.INSUFFICIENT_RIGHTS:
      return (
        <FormattedMessage
          {...messages.insufficientRepoAccessRightsDescription}
        />
      );
    case CREATE_BRANCH_ERROR_TYPE.OTHER:
      // For these errors we simply show the message returned in API response
      return <span>{error.message}</span>;
    default:
    case CREATE_BRANCH_ERROR_TYPE.GENERIC:
      return (
        <FormattedMessage
          {...messages.genericErrorDescription}
          values={{
            supportLink: (
              <a target="_blank" href={urls.external.support}>
                <FormattedMessage {...messages.supportLink} />
              </a>
            ),
          }}
        />
      );
  }
};

export const CreateBranchErrorFlag = ({
  ...otherProps
}: ComponentFlagProps) => {
  const intl = useIntl();
  const error = useSelector((state: BucketState) => state.createBranch.error);

  if (!error) {
    return null;
  }
  const message = errorMessage(error);

  if (!message) {
    // If there is no message to show, we don't render the flag
    return null;
  }

  return (
    <Flag
      {...otherProps}
      icon={
        <StatusErrorIcon
          spacing="spacious"
          label="error"
          color={token('color.icon.danger')}
        />
      }
      title={intl.formatMessage(messages.createBranchErrorTitle)}
      description={message}
    />
  );
};

export const LoadRepositoriesErrorFlag = ({ id }: ComponentFlagProps) => {
  const intl = useIntl();

  return (
    <AutoDismissFlag
      id={id}
      icon={
        <StatusErrorIcon
          spacing="spacious"
          label="error"
          color={token('color.icon.danger')}
        />
      }
      title={intl.formatMessage(messages.loadRepositoriesErrorTitle)}
      description={
        <FormattedMessage
          {...messages.genericErrorDescription}
          values={{
            supportLink: (
              <a target="_blank" href={urls.external.support}>
                <FormattedMessage {...messages.supportLink} />
              </a>
            ),
          }}
        />
      }
    />
  );
};
